import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, Button } from '@medikei/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import {
  Search,
  Eye,
  ChevronLeft,
  ChevronRight,
  Wallet,
  TrendingUp,
  Clock,
  CheckCircle,
  ArrowDownLeft,
  ArrowUpRight,
  User,
  Stethoscope,
  Copy,
  Check,
} from 'lucide-react';
import { httpClient } from '@/api';

interface WalletSummary {
  totalBalance: number;
  availableBalance: number;
  pendingBalance: number;
  blockedBalance: number;
}

interface WalletTransaction {
  id: string;
  userId: string;
  userName: string;
  userEmail?: string;
  userType: 'DOCTOR' | 'PATIENT' | 'SYSTEM';
  type: 'CREDIT' | 'DEBIT';
  operation: string;
  amount: number;
  balanceAfter: number;
  status: 'COMPLETED' | 'PENDING' | 'FAILED';
  date: string;
  description: string;
  referenceId?: string | null;
}

interface WalletWithdrawal {
  id: string;
  doctorName: string;
  doctorEmail: string;
  amount: number;
  pixKey: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  requestedAt: string;
  processedAt?: string;
}

const translateOperation = (op?: string): string => {
  if (!op) return 'Transferência';
  const upper = op.toUpperCase();
  if (upper === 'PAYMENT' || upper === 'CONSULTATION') return 'Pagamento de Consulta';
  if (upper === 'WITHDRAWAL' || upper === 'PAYOUT') return 'Saque PIX';
  if (upper === 'DEPOSIT') return 'Depósito';
  if (upper === 'REFUND') return 'Estorno';
  if (upper === 'COMMISSION' || upper === 'AFFILIATE') return 'Comissão de Afiliado';
  if (upper === 'WALLET') return 'Crédito em Carteira';
  if (upper === 'TRANSFER') return 'Transferência';
  return op;
};

const translateStatus = (st?: string): string => {
  if (!st) return 'Concluído';
  const upper = st.toUpperCase();
  if (upper === 'COMPLETED' || upper === 'APPROVED') return 'Concluído';
  if (upper === 'PENDING' || upper === 'PROCESSING') return 'Pendente';
  if (upper === 'FAILED' || upper === 'REJECTED') return 'Falhou';
  if (upper === 'CANCELLED') return 'Cancelado';
  return st;
};

const translateDescription = (desc?: string): string => {
  if (!desc) return 'Sem descrição';
  return desc
    .replace(/Payment approved \(pending release\):?/gi, 'Pagamento aprovado (aguardando liberação):')
    .replace(/Payment approved:?/gi, 'Pagamento aprovado:')
    .replace(/Payment released:?/gi, 'Pagamento liberado:')
    .replace(/Withdrawal processed:?/gi, 'Saque processado:')
    .replace(/Withdrawal requested:?/gi, 'Saque solicitado:')
    .replace(/Withdrawal rejected:?/gi, 'Saque rejeitado:')
    .replace(/Consultation fee:?/gi, 'Honorário de consulta:')
    .replace(/Platform commission:?/gi, 'Comissão da plataforma:')
    .replace(/Refund:?/gi, 'Estorno:');
};

export function AdminWalletPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<'transactions' | 'withdrawals'>('transactions');
  const [filter, setFilter] = useState<'ALL' | 'CREDIT' | 'DEBIT'>('ALL');
  const [selectedTransaction, setSelectedTransaction] = useState<WalletTransaction | null>(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WalletWithdrawal | null>(null);

  const { data: summary } = useQuery<WalletSummary>({
    queryKey: ['admin', 'wallet', 'summary'],
    queryFn: async () => {
      try {
        const walletSummary = await httpClient.get<WalletSummary>('/admin/financial/wallet-summary');
        if (walletSummary && typeof walletSummary.totalBalance === 'number') {
          return walletSummary;
        }
      } catch {
        // Fallback para o overview financeiro
      }
      const data = await httpClient.get<{
        totalRevenue: number;
        totalPayments: number;
        pendingPayouts: number;
        completedPayouts: number;
        totalRefunds: number;
        refundAmount: number;
        totalChargebacks: number;
        chargebackAmount: number;
      }>('/admin/financial/overview');
      return {
        totalBalance: data.totalRevenue || 0,
        availableBalance: (data.totalRevenue || 0) - (data.pendingPayouts || 0),
        pendingBalance: data.pendingPayouts || 0,
        blockedBalance: 0,
      };
    },
  });

  const { data: transactions, isLoading } = useQuery<WalletTransaction[]>({
    queryKey: ['admin', 'wallet', 'transactions', page, filter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      const json = await httpClient.get<{ data: any[] }>(`/admin/financial/wallet-transactions?${params}`);
      return json.data.map((t: any) => ({
        id: t.id,
        userId: t.walletId || 'N/A',
        userName: t.userName || t.doctorName || 'N/A',
        userEmail: t.userEmail || t.doctorEmail,
        userType: (t.userType || 'DOCTOR') as 'DOCTOR' | 'PATIENT' | 'SYSTEM',
        type: t.type === 'CREDIT' || t.type === 'PENDING_CREDIT' ? 'CREDIT' as const : 'DEBIT' as const,
        operation: t.reference || 'WALLET',
        amount: Number(t.amount),
        balanceAfter: Number(t.balanceAfter || 0),
        status: (t.status === 'COMPLETED' || t.status === 'APPROVED' ? 'COMPLETED' : t.status === 'PENDING' ? 'PENDING' : 'FAILED') as 'COMPLETED' | 'PENDING' | 'FAILED',
        date: t.createdAt,
        description: t.description || t.reference || '',
        referenceId: t.referenceId,
      }));
    },
  });

  const { data: withdrawals } = useQuery<WalletWithdrawal[]>({
    queryKey: ['admin', 'wallet', 'withdrawals'],
    queryFn: async () => {
      const params = new URLSearchParams({ page: '1', limit: '20' });
      const json = await httpClient.get<{ data: any[] }>(`/admin/financial/payouts?${params}`);
      return json.data.map((p: any) => ({
        id: p.id,
        doctorName: p.doctorName || 'N/A',
        doctorEmail: p.doctorEmail || 'N/A',
        amount: Number(p.amount),
        pixKey: p.pixKey || 'N/A',
        status: p.status === 'APPROVED' ? 'COMPLETED' : p.status === 'PENDING' ? 'PENDING' : 'FAILED',
        requestedAt: p.createdAt,
        processedAt: p.processedAt,
      }));
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  };

  const [copiedRef, setCopiedRef] = useState(false);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 2000);
  };

  const filteredTransactions = transactions?.filter((t) => {
    const matchesSearch =
      t.userName.toLowerCase().includes(search.toLowerCase()) ||
      (t.userEmail && t.userEmail.toLowerCase().includes(search.toLowerCase())) ||
      t.description.toLowerCase().includes(search.toLowerCase()) ||
      (t.referenceId && t.referenceId.toLowerCase().includes(search.toLowerCase()));
    const matchesFilter = filter === 'ALL' || t.type === filter;
    return matchesSearch && matchesFilter;
  }) || [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Carteira & Gestão Financeira</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Controle integrado de saldos, honorários médicos e transações de pacientes
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-blue-500/10 text-blue-600 rounded-xl">
              <Wallet className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Saldo Total em Carteiras</p>
              <p className="text-2xl font-bold tracking-tight text-foreground">{formatCurrency(summary?.totalBalance || 0)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-emerald-500/10 text-emerald-600 rounded-xl">
              <CheckCircle className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Disponível para Resgate</p>
              <p className="text-2xl font-bold tracking-tight text-emerald-600 dark:text-emerald-400">{formatCurrency(summary?.availableBalance || 0)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-amber-500/10 text-amber-600 rounded-xl">
              <Clock className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Saldo Pendente</p>
              <p className="text-2xl font-bold tracking-tight text-amber-600 dark:text-amber-400">{formatCurrency(summary?.pendingBalance || 0)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-rose-500/10 text-rose-600 rounded-xl">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Saldo Bloqueado / Disputa</p>
              <p className="text-2xl font-bold tracking-tight text-rose-600 dark:text-rose-400">{formatCurrency(summary?.blockedBalance || 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex bg-muted p-1 rounded-xl w-fit">
          <button
            onClick={() => setTab('transactions')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              tab === 'transactions' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Transações em Carteira
          </button>
          <button
            onClick={() => setTab('withdrawals')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              tab === 'withdrawals' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Solicitações de Saques PIX
          </button>
        </div>

        <div className="flex items-center gap-2.5 flex-1 max-w-md sm:ml-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por médico, paciente, descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>

          {tab === 'transactions' && (
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
              className="px-3 py-2 text-xs border border-input bg-background rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium text-foreground cursor-pointer"
            >
              <option value="ALL">Todas operações</option>
              <option value="CREDIT">Apenas Créditos (+)</option>
              <option value="DEBIT">Apenas Débitos (-)</option>
            </select>
          )}
        </div>
      </div>

      {tab === 'transactions' ? (
        <Card className="rounded-2xl border border-border overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/40 border-b border-border text-xs uppercase font-semibold text-muted-foreground tracking-wider">
                  <tr>
                    <th className="px-4 py-3.5 text-left">Beneficiário / Usuário</th>
                    <th className="px-4 py-3.5 text-left">Tipo de Operação</th>
                    <th className="px-4 py-3.5 text-left">Descrição da Transação</th>
                    <th className="px-4 py-3.5 text-left">Data & Hora</th>
                    <th className="px-4 py-3.5 text-left">Valor Movimentado</th>
                    <th className="px-4 py-3.5 text-left">Status</th>
                    <th className="px-4 py-3.5 text-center">Detalhes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-xs">
                  {isLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                          <span>Carregando transações da carteira...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                        Nenhuma transação encontrada com os filtros selecionados.
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map((t) => (
                      <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg shrink-0 ${t.userType === 'DOCTOR' ? 'bg-blue-500/10 text-blue-600' : 'bg-emerald-500/10 text-emerald-600'}`}>
                              {t.userType === 'DOCTOR' ? <Stethoscope className="h-4 w-4" /> : <User className="h-4 w-4" />}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-foreground truncate">{t.userName}</p>
                              <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.2 rounded-md ${
                                t.userType === 'DOCTOR'
                                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                                  : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                              }`}>
                                {t.userType === 'DOCTOR' ? 'Médico' : 'Paciente'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="font-semibold text-foreground block">
                            {translateOperation(t.operation)}
                          </span>
                          <span className="text-[10px] text-muted-foreground uppercase">
                            {t.type === 'CREDIT' ? 'Entrada (+)' : 'Saída (-)'}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-muted-foreground max-w-xs truncate" title={t.description}>
                          {translateDescription(t.description)}
                        </td>
                        <td className="px-4 py-3.5 text-muted-foreground whitespace-nowrap">
                          {formatDate(t.date)}
                        </td>
                        <td className={`px-4 py-3.5 font-extrabold whitespace-nowrap ${t.type === 'CREDIT' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                          {t.type === 'CREDIT' ? '+' : '-'}{formatCurrency(t.amount)}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-bold rounded-full ${
                            t.status === 'COMPLETED'
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                              : t.status === 'PENDING'
                              ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                              : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
                          }`}>
                            {translateStatus(t.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedTransaction(t)}
                            className="h-8 w-8 p-0 rounded-lg text-primary hover:bg-primary/10"
                            title="Ver detalhes da transação"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-4 py-3.5 border-t border-border bg-muted/20 text-xs">
              <p className="text-muted-foreground">
                Mostrando {((page - 1) * 20) + 1} a {Math.min(page * 20, transactions?.length || 0)} de {transactions?.length || 0} transações
              </p>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 w-8 p-0 rounded-lg"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2.5 py-1 bg-primary text-primary-foreground font-bold rounded-lg text-xs">
                  {page}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * 20 >= (transactions?.length || 0)}
                  className="h-8 w-8 p-0 rounded-lg"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-2xl border border-border overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/40 border-b border-border text-xs uppercase font-semibold text-muted-foreground tracking-wider">
                  <tr>
                    <th className="px-4 py-3.5 text-left">Profissional Solicitante</th>
                    <th className="px-4 py-3.5 text-left">Chave PIX Cadastrada</th>
                    <th className="px-4 py-3.5 text-left">Valor do Resgate</th>
                    <th className="px-4 py-3.5 text-left">Data da Solicitação</th>
                    <th className="px-4 py-3.5 text-left">Data de Pagamento</th>
                    <th className="px-4 py-3.5 text-left">Status</th>
                    <th className="px-4 py-3.5 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-xs">
                  {withdrawals?.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                        Nenhuma solicitação de saque registrada até o momento.
                      </td>
                    </tr>
                  ) : (
                    withdrawals?.map((w) => (
                      <tr key={w.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3.5">
                          <p className="font-bold text-foreground">{w.doctorName}</p>
                          <p className="text-[11px] text-muted-foreground">{w.doctorEmail}</p>
                        </td>
                        <td className="px-4 py-3.5 font-mono text-muted-foreground">{w.pixKey}</td>
                        <td className="px-4 py-3.5 font-extrabold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(w.amount)}
                        </td>
                        <td className="px-4 py-3.5 text-muted-foreground">{formatDate(w.requestedAt)}</td>
                        <td className="px-4 py-3.5 text-muted-foreground">{w.processedAt ? formatDate(w.processedAt) : 'Aguardando processamento'}</td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 text-[11px] font-bold rounded-full ${
                            w.status === 'COMPLETED'
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200'
                              : w.status === 'PROCESSING'
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200'
                              : w.status === 'PENDING'
                              ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200'
                              : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200'
                          }`}>
                            {translateStatus(w.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedWithdrawal(w)}
                            className="h-8 w-8 p-0 rounded-lg text-primary hover:bg-primary/10"
                            title="Ver detalhes do saque"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* MODAL DETALHES DA TRANSAÇÃO (REVISADO, COMPLETO E EM PORTUGUÊS) */}
      <Dialog open={!!selectedTransaction} onOpenChange={() => setSelectedTransaction(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl border border-border shadow-2xl bg-card">
          <DialogHeader className="px-6 py-4 border-b border-border bg-muted/20">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${selectedTransaction?.type === 'CREDIT' ? 'bg-emerald-500/15 text-emerald-600' : 'bg-rose-500/15 text-rose-600'}`}>
                {selectedTransaction?.type === 'CREDIT' ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-foreground">Detalhes da Transação</DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  ID: <span className="font-mono">{selectedTransaction?.id}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          {selectedTransaction && (
            <div className="p-6 space-y-5">
              {/* Card Destaque de Valor */}
              <div className="p-4 rounded-xl bg-muted/40 border border-border/80 flex items-center justify-between">
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block">
                    Valor Movimentado
                  </span>
                  <div className={`text-2xl font-black tracking-tight ${selectedTransaction.type === 'CREDIT' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {selectedTransaction.type === 'CREDIT' ? '+' : '-'}{formatCurrency(selectedTransaction.amount)}
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-bold rounded-full ${
                    selectedTransaction.type === 'CREDIT'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                      : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                  }`}>
                    {selectedTransaction.type === 'CREDIT' ? 'Entrada (Crédito)' : 'Saída (Débito)'}
                  </span>
                  <div>
                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-md bg-muted text-foreground border border-border">
                      {translateStatus(selectedTransaction.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Informações Estruturadas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">Beneficiário</span>
                  <p className="font-bold text-sm text-foreground flex items-center gap-1.5">
                    {selectedTransaction.userName}
                    <span className="text-[10px] font-semibold px-1.5 py-0.2 rounded-md bg-muted text-muted-foreground border border-border">
                      {selectedTransaction.userType === 'DOCTOR' ? 'Médico' : selectedTransaction.userType === 'PATIENT' ? 'Paciente' : 'Sistema'}
                    </span>
                  </p>
                  {selectedTransaction.userEmail && (
                    <p className="text-[11px] text-muted-foreground">{selectedTransaction.userEmail}</p>
                  )}
                </div>

                <div className="p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">Operação Financeira</span>
                  <p className="font-bold text-sm text-foreground">
                    {translateOperation(selectedTransaction.operation)}
                  </p>
                  <p className="text-[11px] text-muted-foreground font-mono">
                    Ref: {selectedTransaction.operation}
                  </p>
                </div>

                <div className="p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">Data & Horário de Registro</span>
                  <p className="font-bold text-foreground">
                    {new Date(selectedTransaction.date).toLocaleString('pt-BR')}
                  </p>
                </div>

                <div className="p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">ID de Rastreabilidade</span>
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-mono text-muted-foreground truncate" title={selectedTransaction.referenceId || selectedTransaction.id}>
                      {selectedTransaction.referenceId || selectedTransaction.id}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(selectedTransaction.referenceId || selectedTransaction.id)}
                      className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                      title="Copiar código de referência"
                    >
                      {copiedRef ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>

                <div className="sm:col-span-2 p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">Descrição Detalhada</span>
                  <p className="font-medium text-foreground leading-relaxed">
                    {translateDescription(selectedTransaction.description)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="px-6 py-3.5 border-t border-border bg-muted/20">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedTransaction(null)}
              className="w-full sm:w-auto text-xs font-semibold"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL DETALHES DO SAQUE (REVISADO, COMPLETO E EM PORTUGUÊS) */}
      <Dialog open={!!selectedWithdrawal} onOpenChange={() => setSelectedWithdrawal(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl border border-border shadow-2xl bg-card">
          <DialogHeader className="px-6 py-4 border-b border-border bg-muted/20">
            <DialogTitle className="text-lg font-bold text-foreground">Detalhes do Resgate PIX</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Protocolo: <span className="font-mono">{selectedWithdrawal?.id}</span>
            </p>
          </DialogHeader>

          {selectedWithdrawal && (
            <div className="p-6 space-y-5">
              {/* Card Destaque de Valor */}
              <div className="p-4 rounded-xl bg-muted/40 border border-border/80 flex items-center justify-between">
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block">
                    Valor a Transferir
                  </span>
                  <div className="text-2xl font-black tracking-tight text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(selectedWithdrawal.amount)}
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 text-xs font-bold rounded-full ${
                  selectedWithdrawal.status === 'COMPLETED'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                    : selectedWithdrawal.status === 'PROCESSING'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                }`}>
                  {translateStatus(selectedWithdrawal.status)}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">Médico Solicitante</span>
                  <p className="font-bold text-foreground">{selectedWithdrawal.doctorName}</p>
                  <p className="text-[11px] text-muted-foreground">{selectedWithdrawal.doctorEmail}</p>
                </div>

                <div className="p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">Chave PIX de Destino</span>
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-mono font-bold text-foreground truncate" title={selectedWithdrawal.pixKey}>
                      {selectedWithdrawal.pixKey}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(selectedWithdrawal.pixKey)}
                      className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                      title="Copiar chave PIX"
                    >
                      {copiedRef ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>

                <div className="p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">Data da Solicitação</span>
                  <p className="font-bold text-foreground">
                    {new Date(selectedWithdrawal.requestedAt).toLocaleString('pt-BR')}
                  </p>
                </div>

                <div className="p-3 rounded-xl border border-border bg-background space-y-1">
                  <span className="text-muted-foreground font-medium block">Data do Processamento</span>
                  <p className="font-bold text-foreground">
                    {selectedWithdrawal.processedAt ? new Date(selectedWithdrawal.processedAt).toLocaleString('pt-BR') : 'Aguardando liquidação'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="px-6 py-3.5 border-t border-border bg-muted/20">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedWithdrawal(null)}
              className="w-full sm:w-auto text-xs font-semibold"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
